import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BulkBuAction, MemberService } from '../../../core/services/member.service';
import { BusinessUnitStore } from '../../../core/stores/business-unit.store';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';

export interface BulkAssignResult {
  modifiedCount: number;
  notFoundCount: number;
  action: BulkBuAction;
  businessUnitIds: string[];
}

@Component({
  selector: 'app-bulk-assign-bu-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, SpinnerComponent],
  templateUrl: './bulk-assign-bu-modal.component.html'
})
export class BulkAssignBuModalComponent implements OnChanges {
  private memberService = inject(MemberService);
  private buStore = inject(BusinessUnitStore);

  @Input() open = false;
  @Input() memberIds: string[] = [];
  @Input() defaultAction: BulkBuAction = 'add';

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<BulkAssignResult>();

  readonly action = signal<BulkBuAction>('add');
  readonly selectedBuIds = signal<Set<string>>(new Set());
  readonly errorMessage = signal('');
  readonly isSaving = signal(false);

  readonly activeUnits = this.buStore.activeUnits;
  readonly memberCount = computed(() => this.memberIds.length);

  ngOnChanges(changes: SimpleChanges): void {
    const openChange = changes['open'];
    if (openChange && openChange.currentValue === true && openChange.previousValue !== true) {
      this.buStore.loadOnce();
      this.action.set(this.defaultAction);
      this.selectedBuIds.set(new Set());
      this.errorMessage.set('');
      this.isSaving.set(false);
    }
  }

  setAction(a: BulkBuAction): void {
    this.action.set(a);
  }

  isBuSelected(id: string): boolean {
    return this.selectedBuIds().has(id);
  }

  toggleBu(id: string): void {
    const set = new Set(this.selectedBuIds());
    if (set.has(id)) set.delete(id);
    else set.add(id);
    this.selectedBuIds.set(set);
  }

  title(): string {
    switch (this.action()) {
      case 'remove':  return `Remove ${this.memberCount()} member(s) from committees`;
      case 'replace': return `Replace committees for ${this.memberCount()} member(s)`;
      default:        return `Assign ${this.memberCount()} member(s) to committees`;
    }
  }

  confirmLabel(): string {
    switch (this.action()) {
      case 'remove':  return 'Remove';
      case 'replace': return 'Replace';
      default:        return 'Assign';
    }
  }

  close(): void {
    if (this.isSaving()) return;
    this.closed.emit();
  }

  submit(): void {
    const ids = Array.from(this.selectedBuIds());
    if (ids.length === 0) {
      this.errorMessage.set('Select at least one committee.');
      return;
    }
    if (this.memberIds.length === 0) {
      this.errorMessage.set('No members selected.');
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set('');
    const action = this.action();

    this.memberService.bulkAssignBusinessUnits({
      memberIds: this.memberIds,
      businessUnitIds: ids,
      action
    }).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        if (res.success) {
          this.saved.emit({
            modifiedCount: res.modifiedCount ?? 0,
            notFoundCount: res.notFoundCount ?? 0,
            action,
            businessUnitIds: ids
          });
        } else {
          this.errorMessage.set(res.message || 'Bulk assignment failed.');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.errorMessage.set(err?.error?.message || 'Bulk assignment failed.');
      }
    });
  }
}
